import { useCallback, useContext, useState } from 'react';
import Cropper from 'react-easy-crop';
import './ImageCrop.css';
import { Slider } from '@mui/material';
import { Button } from '../../components/UI';
import getCroppedImg from './utils';
import { UploadImageContext } from '../../context/UploadImage';
import { UploadImageEnum } from './UploadImage.enum';
import { uploadProfileAvatar } from '../../http/upload-image.service';
import { useTranslation } from 'react-i18next';

export interface ImageCropPropsModel {
  image: string;
  cropShape?: 'rect' | 'round';
  fileName: string;
}

const ImageCrop = (props: ImageCropPropsModel) => {
  const { t } = useTranslation();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1 as any);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null as any);
  const { setCroppedImage, setUploadImageStep, profileId } =
    useContext(UploadImageContext);
  const [loading, setLoading] = useState(false);
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const cropImage = async () => {
    const croppedImage = await getCroppedImg(props.image, croppedAreaPixels);
    return croppedImage;
  };

  const remove = () => {
    setUploadImageStep(UploadImageEnum.CHOOSE_PROFILE_PICTURE);
  };

  const changeImage = () => {
    setUploadImageStep(UploadImageEnum.DROPZONE);
  };

  const saveImage = async () => {
    setLoading(true);
    const croppedImage = await cropImage();
    const res: any = await uploadProfileAvatar(
      props.fileName,
      croppedImage,
      profileId
    );
    setCroppedImage({ url: croppedImage, id: res.data.completeMediaUpload.id });
    setLoading(false);
    setUploadImageStep(UploadImageEnum.CHOOSE_PROFILE_PICTURE);
  };

  return (
    <div>
      <div className="crop-container relative w-full lg:w-600 h-96 ">
        <Cropper
          image={props.image}
          crop={crop}
          zoom={zoom}
          cropShape={props.cropShape}
          aspect={1}
          zoomWithScroll={false}
          objectFit={'horizontal-cover'}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
        />
      </div>
      <div className="bg-grey-90 h-16 w-full flex flex-col justify-center px-8">
        <span className="text-white -ml-2 text-sm">
          {t('uploadImage.imageCrop.zoom')}
        </span>
        <Slider
          value={zoom}
          min={1}
          max={3}
          step={0.1}
          aria-labelledby="Zoom"
          onChange={(e, zoom) => setZoom(zoom)}
        />
      </div>
      <div className="p-5 flex justify-between items-center">
        <Button
          type="button"
          variant="transparent"
          buttonClass="text-sm font-semibold"
          onClick={() => remove()}
        >
          {t('uploadImage.imageCrop.remove')}
        </Button>
        <div className="flex">
          <Button
            type="button"
            variant="transparent"
            onClick={() => changeImage()}
          >
            {t('uploadImage.imageCrop.change')}
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={loading}
            onClick={() => saveImage()}
          >
            {t('uploadImage.imageCrop.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageCrop;
